import { ReglagesPastilles, ReglagesNotificationsExternesPersonnelles } from './NotificationsPersonnelles.js';
import { useEffect, useState, type JSX } from 'react';
import { etatInterrupteurPersonnel } from './interrupteurPersonnel.js';
import {
  Button,
  ConfirmDialog,
  SettingsActions,
  SettingsEmpty,
  SettingsGroup,
  SettingsInput,
  SettingsMenu,
  SettingsMessage,
  SettingsPanel,
  SettingsRow,
  SettingsSave,
  SettingsSection,
  SettingsTable,
  SettingsTextarea,
  StatusBadge,
  Switch,
  useNotificationsPort,
  type BilanNotifierView,
  type NotificationCanalView,
  type NotificationRegleInputView,
  type NotificationRegleView,
  type NotificationStatutView,
  type NotificationView,
  type StatusBadgeTone,
} from '@taki/ui';

/**
 * PARAMETRES > NOTIFICATIONS : le panneau du moteur de notifications
 * (docs/NOTIFICATIONS.md), en six blocs, le personnel d'abord.
 *
 *   1. « Mes notifications » : CHAQUE membre regle ce qu'il accepte (canaux,
 *      heures de silence, regles dont il se desinscrit), et peut s'envoyer un
 *      essai.
 *   2. « Pastilles » (personnel) et 3. « Notifications internes » : posees
 *      vides le 06/10/2026, remplies par le chantier des notifications v2.
 *   4. « Notifications externes » (owner, admin ; les autres lisent qui la
 *      regle) : e-mail et son canevas, Slack ; WhatsApp n'est allume que par
 *      l'OWNER. L'ancienne section « Canaux ».
 *   5. « Regles » (owner, admin) : la liste, un formulaire court pour en
 *      ecrire une, et « Tester sur une fiche ».
 *   Plus de « Journal » ici (11/10/2026) : les messages de l'org se lisent
 *   dans l'ecran Journal ; la relance des echecs reste automatique, au serveur.
 *
 * Le panneau ne connait pas Firebase : tout passe par `useNotificationsPort`
 * (`@taki/ui`), que l'hote fabrique a partir du SDK. Un geste refuse par les
 * rules revient en phrase, dans le bloc qui l'a demande.
 */

const CANAUX: Array<{ id: Exclude<NotificationCanalView, 'interne'>; label: string }> = [
  { id: 'email', label: 'E-mail' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'slack', label: 'Slack' },
];

/** Les canaux qu'une regle peut citer : les trois exterieurs, plus la boite interne (la cloche). */
const CANAUX_DE_REGLE: Array<{ id: NotificationCanalView; label: string }> = [...CANAUX, { id: 'interne', label: 'Dans MyTaki' }];

const STATUTS: Record<NotificationStatutView, { label: string; tone: StatusBadgeTone }> = {
  enFile: { label: 'En file', tone: 'neutral' },
  envoye: { label: 'Envoyé', tone: 'accent' },
  livre: { label: 'Livré', tone: 'success' },
  echec: { label: 'Échec, à rejouer', tone: 'danger' },
  abandonne: { label: 'Abandonné', tone: 'danger' },
};

function phrase(cause: unknown): string {
  return cause instanceof Error && cause.message !== '' ? cause.message : String(cause);
}

function bilanEnPhrase(bilan: BilanNotifierView): string {
  const parts = [`${bilan.crees} mis en file`];
  if (bilan.existants > 0) parts.push(`${bilan.existants} déjà en file`);
  if (bilan.ecartes.length > 0) parts.push(`écartés : ${bilan.ecartes.map((e) => `${e.destinataire} (${e.raison})`).join(' ; ')}`);
  return parts.join(', ') + '.';
}

function dateCourte(d: Date | null): string {
  return d === null ? '' : new Intl.DateTimeFormat('fr-LU', { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

/** Les colonnes Date et Statut des notifications recentes. */
const COLONNES_ENVOI = [{ label: 'Date', fin: true }, { label: 'Statut', fin: true }] as const;

function cellulesEnvoi(n: NotificationView): JSX.Element[] {
  return [
    <span key="date" className="whitespace-nowrap text-xs text-ink-500">{dateCourte(n.createdAt)}</span>,
    <StatusBadge key="statut" tone={STATUTS[n.statut].tone} label={STATUTS[n.statut].label} />,
  ];
}

/* -------------------------------------------------------------------------- */
/* Mes notifications                                                           */
/* -------------------------------------------------------------------------- */

function InterrupteurPersonnel({ label, etat, onChange }: {
  label: string;
  etat: ReturnType<typeof etatInterrupteurPersonnel>;
  onChange: (value: boolean) => void;
}): JSX.Element {
  return (
    <SettingsRow label={label} lock={etat.locked ? etat.lockedReason ?? etat.hint ?? "À activer pour l’organisation" : undefined}>
      <Switch
        checked={etat.checked}
        locked={etat.locked}
        {...(etat.lockedReason ? { lockedReason: etat.lockedReason } : {})}
        onChange={onChange}
        aria-label={`Recevoir par ${label}`}
      />
    </SettingsRow>
  );
}

function MesNotifications(): JSX.Element {
  const port = useNotificationsPort();
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [miens, setMiens] = useState<NotificationView[]>([]);

  useEffect(() => {
    if (!port?.ready) return;
    void port.miens(10).then(setMiens).catch(() => setMiens([]));
  }, [port?.ready, avis]);

  if (port === null) return <SettingsSection title="Mes notifications" contextLabel="Votre compte"><SettingsEmpty>Notifications indisponibles.</SettingsEmpty></SettingsSection>;
  const prefs = port.prefs;
  const silence = prefs.silence;

  const ecrire = async (patch: Parameters<typeof port.enregistrerPrefs>[0]): Promise<void> => {
    setErreur(null);
    try { await port.enregistrerPrefs(patch); }
    catch (cause) { setErreur(phrase(cause)); }
  };
  const essai = async (): Promise<void> => {
    setOccupe(true);
    setErreur(null);
    setAvis(null);
    try { setAvis(`Essai envoyé : ${bilanEnPhrase(await port.envoyerEssai('email'))}`); }
    catch (cause) { setErreur(phrase(cause)); }
    finally { setOccupe(false); }
  };

  return (
    <SettingsSection title="Mes notifications" contextLabel="Votre compte">
      {CANAUX.map((canal) => (
        <InterrupteurPersonnel
          key={canal.id}
          label={canal.label}
          etat={etatInterrupteurPersonnel(port.reglages.canaux[canal.id].actif, prefs.canaux[canal.id], port.ready)}
          onChange={(value: boolean) => void ecrire({ canaux: { ...prefs.canaux, [canal.id]: value } })}
        />
      ))}
      <SettingsRow label="Dans MyTaki">
        <Switch
          checked={prefs.canaux.interne !== false}
          locked={!port.ready}
          onChange={(value: boolean) => void ecrire({ canaux: { ...prefs.canaux, interne: value } })}
          aria-label="Recevoir dans MyTaki"
        />
      </SettingsRow>
      <SettingsRow label="Heures de silence" hint="Envois reportés">
        <div className="flex w-full min-w-0 items-center gap-2">
          <SettingsInput
            type="time"
            value={silence?.de ?? ''}
            aria-label="Début du silence"
            onChange={(event) => void ecrire({ silence: event.target.value ? { de: event.target.value, a: silence?.a ?? '07:00' } : null })}
          />
          <span className="text-sm text-ink-500">à</span>
          <SettingsInput
            type="time"
            value={silence?.a ?? ''}
            aria-label="Fin du silence"
            onChange={(event) => void ecrire({ silence: event.target.value ? { de: silence?.de ?? '22:00', a: event.target.value } : null })}
          />
        </div>
      </SettingsRow>
      {port.regles.filter((r) => r.actif).map((regle) => (
        <SettingsRow key={regle.id} label={regle.nom + (regle.famille === 'conversations' || regle.famille === 'messages' && regle.type === 'conversation' ? ' (inapplicable aux conversations)' : '')} hint={`Règle ${regle.declencheur === 'planifie' ? 'planifiée' : regle.declencheur === 'action' ? 'à la demande' : 'sur événement'} sur ${regle.famille}`}>
          <Switch
            checked={!prefs.desinscrit.includes(regle.id)}
            onChange={(value: boolean) =>
              void ecrire({ desinscrit: value ? prefs.desinscrit.filter((id) => id !== regle.id) : [...prefs.desinscrit, regle.id] })}
            aria-label={`Recevoir « ${regle.nom} »`}
          />
        </SettingsRow>
      ))}
      <SettingsRow label="Tester par e-mail">
        <Button variant="secondary" size="sm" disabled={occupe || !port.ready} onClick={() => void essai()}>
          {occupe ? 'Envoi...' : 'Envoyer un essai'}
        </Button>
      </SettingsRow>
      {miens.length > 0 && (
        <SettingsTable
          label="Mes notifications récentes"
          columns={[{ label: 'Sujet' }, ...COLONNES_ENVOI]}
          rows={miens.map((n) => ({
            id: n.id,
            cells: [<span key="sujet" className="block truncate text-ink-700">{n.sujet}</span>, ...cellulesEnvoi(n)],
          }))}
        />
      )}
      {avis && <SettingsMessage>{avis}</SettingsMessage>}
      {erreur && <SettingsMessage tone="error">{erreur}</SettingsMessage>}
    </SettingsSection>
  );
}

/* -------------------------------------------------------------------------- */
/* Pastilles et notifications internes : sections posees, contenu a venir     */
/* -------------------------------------------------------------------------- */

/*
 * LES TROIS SECTIONS NOMMEES DE L'ONGLET (06/10/2026) : Pastilles, Notifications
 * internes, Notifications externes. Les deux premieres sont posees VIDES, avec
 * leur titre et leur phrase : le chantier des notifications v2 les remplit (dont
 * l'interrupteur personnel « Mascotte animee », sous Pastilles) sans toucher a
 * l'ordre du panneau.
 */

/** Pastilles : les compteurs et signaux visuels de MON ecran (personnel). */
export function SectionPastilles(): JSX.Element { return <ReglagesPastilles />; }

/** Notifications internes : ce qui arrive dans MyTaki (cloche, centre de communication). */

/* -------------------------------------------------------------------------- */
/* Notifications externes (les canaux d'envoi de l'organisation)               */
/* -------------------------------------------------------------------------- */

/**
 * Notifications externes : ce qui sort de MyTaki (e-mail et son canevas,
 * WhatsApp, Slack). Les lignes sont celles de l'ancienne section « Canaux »,
 * inchangees, reservees a qui gere ; les autres lisent qui la regle.
 */
export function SectionNotificationsExternes(): JSX.Element {
  const port = useNotificationsPort();
  return (
    <SettingsSection
      title="Notifications externes"
      contextLabel="Compte et organisation"
    >
      <ReglagesNotificationsExternesPersonnelles />
      <SettingsGroup title="Canaux de l’organisation">
      {port?.canManage ? (
        <Canaux />
      ) : (
        <SettingsRow label="E-mail, WhatsApp, Slack" lock="Propriétaires et administrateurs uniquement">{null}</SettingsRow>
      )}
      </SettingsGroup>
    </SettingsSection>
  );
}

/** Les lignes des canaux d'envoi, sans cadre : `SectionNotificationsExternes` le pose. */
function Canaux(): JSX.Element | null {
  const port = useNotificationsPort();
  const [erreur, setErreur] = useState<string | null>(null);
  const [webhook, setWebhook] = useState<string>('');
  const [replyTo, setReplyTo] = useState<string>('');
  const [nom, setNom] = useState<string>('');
  const [pied, setPied] = useState<string>('');

  useEffect(() => {
    if (!port) return;
    setWebhook(port.reglages.canaux.slack.webhookUrl ?? '');
    setReplyTo(port.reglages.replyTo ?? '');
    setNom(port.reglages.nomExpediteur ?? '');
    setPied(port.reglages.piedDePage ?? '');
  }, [port?.reglages]);

  if (port === null || !port.canManage) return null;
  const ecrire = async (patch: Parameters<typeof port.enregistrerReglages>[0]): Promise<void> => {
    setErreur(null);
    try { await port.enregistrerReglages(patch); }
    catch (cause) { setErreur(phrase(cause)); }
  };

  return (
    <>
      <SettingsRow label="E-mail" hint="notifications@mytaki.app">
        <Switch checked={port.reglages.canaux.email.actif} onChange={(v: boolean) => void ecrire({ canaux: { email: v } })} aria-label="Canal e-mail" />
      </SettingsRow>
      <SettingsRow label="Adresse de réponse">
        <SettingsInput type="email" value={replyTo} placeholder="contact@votre-org.lu" onChange={(e) => setReplyTo(e.target.value)} onBlur={() => void ecrire({ replyTo: replyTo.trim() || null })} />
      </SettingsRow>
      <SettingsRow label="Nom d'expéditeur">
        <SettingsInput value={nom} placeholder="Nom de l'organisation" onChange={(e) => setNom(e.target.value)} onBlur={() => void ecrire({ nomExpediteur: nom.trim() || null })} />
      </SettingsRow>
      <SettingsRow label="Pied de page">
        <SettingsInput value={pied} placeholder="Votre organisation SARL, Luxembourg" onChange={(e) => setPied(e.target.value)} onBlur={() => void ecrire({ piedDePage: pied.trim() || null })} />
      </SettingsRow>
      <SettingsRow
        label="WhatsApp"
        lock={port.isOwner ? undefined : 'Propriétaire uniquement'}
      >
        <Switch checked={port.reglages.canaux.whatsapp.actif} locked={!port.isOwner} lockedReason="Propriétaire uniquement" onChange={(v: boolean) => void ecrire({ canaux: { whatsapp: v } })} aria-label="Canal WhatsApp" />
      </SettingsRow>
      <SettingsRow label="Slack">
        <Switch checked={port.reglages.canaux.slack.actif} onChange={(v: boolean) => void ecrire({ canaux: { slack: { actif: v } } })} aria-label="Canal Slack" />
      </SettingsRow>
      <SettingsRow label="Adresse Slack">
        <SettingsInput value={webhook} placeholder="https://hooks.slack.com/services/..." onChange={(e) => setWebhook(e.target.value)} onBlur={() => void ecrire({ canaux: { slack: { actif: port.reglages.canaux.slack.actif, webhookUrl: webhook.trim() || null } } })} />
      </SettingsRow>
      {erreur && <SettingsMessage tone="error">{erreur}</SettingsMessage>}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Regles                                                                      */
/* -------------------------------------------------------------------------- */

const DECLENCHEURS = [
  { value: 'evenement', label: 'Sur un événement de fiche' },
  { value: 'planifie', label: 'À une date de la fiche' },
  { value: 'action', label: 'À la demande (bouton, Lia)' },
];

const CONDITIONS = [
  { value: 'cree', label: 'La fiche est créée' },
  { value: 'champChange', label: 'Un champ change' },
  { value: 'statutDevient', label: 'Un champ prend une valeur' },
];

const REGLE_VIDE: NotificationRegleInputView = {
  nom: '',
  actif: false,
  declencheur: 'evenement',
  famille: '',
  condition: 'cree',
  gabarit: { sujet: '', corps: '' },
  destinataires: [{ kind: 'roles', roles: ['owner', 'admin'] }],
  canaux: ['email'],
};

function FormulaireRegle({ initial, onEnregistrer, onAnnuler }: { initial: NotificationRegleInputView; onEnregistrer: (r: NotificationRegleInputView) => Promise<void>; onAnnuler: () => void }): JSX.Element {
  const [r, setR] = useState<NotificationRegleInputView>(initial);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const roles = r.destinataires.find((d) => d.kind === 'roles');
  const champ = r.destinataires.find((d) => d.kind === 'champ');
  const poser = (patch: Partial<NotificationRegleInputView>): void => setR((courant) => ({ ...courant, ...patch }));
  const poserRoles = (role: 'owner' | 'admin' | 'member' | 'viewer', coche: boolean): void => {
    const liste = new Set(roles?.kind === 'roles' ? roles.roles : []);
    if (coche) liste.add(role); else liste.delete(role);
    poser({ destinataires: [{ kind: 'roles', roles: [...liste] }, ...r.destinataires.filter((d) => d.kind !== 'roles')] });
  };
  const poserChamp = (valeur: string): void => {
    const sans = r.destinataires.filter((d) => d.kind !== 'champ');
    poser({ destinataires: valeur.trim() ? [...sans, { kind: 'champ', champ: valeur.trim() }] : sans });
  };
  const enregistrer = async (): Promise<void> => {
    setOccupe(true);
    setErreur(null);
    const propre: NotificationRegleInputView = {
      ...r,
      nom: r.nom.trim(),
      famille: r.famille.trim(),
      destinataires: r.destinataires.filter((d) => d.kind !== 'roles' || d.roles.length > 0),
    };
    if (propre.type === '') delete propre.type;
    if (propre.declencheur !== 'evenement') { delete propre.condition; delete propre.champ; delete propre.valeur; }
    if (propre.declencheur !== 'planifie') { delete propre.champDate; delete propre.decalageJours; delete propre.heure; }
    if (propre.condition === 'cree') { delete propre.champ; delete propre.valeur; }
    try { await onEnregistrer(propre); }
    catch (cause) { setErreur(phrase(cause)); }
    finally { setOccupe(false); }
  };

  return (
    <SettingsGroup title="Règle">
      <SettingsRow label="Nom"><SettingsInput value={r.nom} placeholder="Nouveau ticket" onChange={(e) => poser({ nom: e.target.value })} /></SettingsRow>
      <SettingsRow label="Déclencheur">
        <SettingsMenu label="Déclencheur" value={r.declencheur} options={DECLENCHEURS} onChange={(v) => poser({ declencheur: v as NotificationRegleInputView['declencheur'] })} />
      </SettingsRow>
      <SettingsRow label="Famille" hint="tickets, bookings, contacts…"><SettingsInput value={r.famille} placeholder="tickets" onChange={(e) => poser({ famille: e.target.value })} /></SettingsRow>
      <SettingsRow label="Type" hint="Sous-type, facultatif"><SettingsInput value={r.type ?? ''} placeholder="incident" onChange={(e) => poser({ type: e.target.value })} /></SettingsRow>
      {r.declencheur === 'evenement' && (
        <>
          <SettingsRow label="Condition">
            <SettingsMenu label="Condition" value={r.condition ?? 'cree'} options={CONDITIONS} onChange={(v) => poser({ condition: v as NotificationRegleInputView['condition'] })} />
          </SettingsRow>
          {r.condition !== 'cree' && (
            <SettingsRow label="Champ" hint="status, customFields.etat..."><SettingsInput value={r.champ ?? ''} onChange={(e) => poser({ champ: e.target.value })} /></SettingsRow>
          )}
          {r.condition === 'statutDevient' && (
            <SettingsRow label="Valeur attendue"><SettingsInput value={r.valeur ?? ''} onChange={(e) => poser({ valeur: e.target.value })} /></SettingsRow>
          )}
        </>
      )}
      {r.declencheur === 'planifie' && (
        <>
          <SettingsRow label="Champ date" hint="dueDate, customFields.date…"><SettingsInput value={r.champDate ?? ''} onChange={(e) => poser({ champDate: e.target.value })} /></SettingsRow>
          <SettingsRow label="Décalage (jours)" hint="Négatif : avant"><SettingsInput type="number" value={r.decalageJours ?? 0} onChange={(e) => poser({ decalageJours: Number(e.target.value) || 0 })} /></SettingsRow>
          <SettingsRow label="Heure"><SettingsInput type="time" value={r.heure ?? '09:00'} onChange={(e) => poser({ heure: e.target.value })} /></SettingsRow>
        </>
      )}
      <SettingsRow label="Sujet" hint="{{title}}, {{org.name}}, {{membre.displayName}}, {{lien}}"><SettingsInput value={r.gabarit.sujet} onChange={(e) => poser({ gabarit: { ...r.gabarit, sujet: e.target.value } })} /></SettingsRow>
      <SettingsRow label="Corps">
        <SettingsTextarea aria-label="Corps" rows={4} value={r.gabarit.corps} onChange={(e) => poser({ gabarit: { ...r.gabarit, corps: e.target.value } })} />
      </SettingsRow>
      <SettingsTable
        label="Destinataires par rôle"
        columns={[{ label: 'Destinataires par rôle' }, { label: 'Destinataire', cache: true, fin: true }]}
        rows={(['owner', 'admin', 'member', 'viewer'] as const).map((role) => ({
          id: role,
          cells: [role, <Switch key="role" aria-label={role} checked={roles?.kind === 'roles' && roles.roles.includes(role)} onChange={(coche: boolean) => poserRoles(role, coche)} />],
        }))}
      />
      <SettingsRow label="Champ e-mail de la fiche" hint="customFields.email"><SettingsInput value={champ?.kind === 'champ' ? champ.champ : ''} onChange={(e) => poserChamp(e.target.value)} /></SettingsRow>
      <SettingsTable
        label="Canaux"
        columns={[{ label: 'Canaux' }, { label: 'Utilisé', cache: true, fin: true }]}
        rows={CANAUX_DE_REGLE.map((c) => ({
          id: c.id,
          cells: [c.label, <Switch key="canal" aria-label={c.label} checked={r.canaux.includes(c.id)} onChange={(coche: boolean) => poser({ canaux: coche ? [...r.canaux, c.id] : r.canaux.filter((x) => x !== c.id) })} />],
        }))}
      />
      <SettingsRow label="App et porte" hint="Pour {{lien}}">
        <div className="flex w-full min-w-0 gap-2">
          <SettingsInput value={r.appId ?? ''} placeholder="support" onChange={(e) => poser({ appId: e.target.value || undefined })} />
          <SettingsInput value={r.porteId ?? ''} placeholder="tickets" onChange={(e) => poser({ porteId: e.target.value || undefined })} />
        </div>
      </SettingsRow>
      <SettingsRow label="Active"><Switch checked={r.actif} onChange={(v: boolean) => poser({ actif: v })} aria-label="Règle active" /></SettingsRow>
      {erreur && <SettingsMessage tone="error">{erreur}</SettingsMessage>}
      <SettingsActions>
        <SettingsSave busy={occupe} onSave={() => void enregistrer()} />
        <Button size="sm" variant="secondary" onClick={onAnnuler}>Annuler</Button>
      </SettingsActions>
    </SettingsGroup>
  );
}

function Regles(): JSX.Element | null {
  const port = useNotificationsPort();
  const [edition, setEdition] = useState<{ id: string | null; valeur: NotificationRegleInputView } | null>(null);
  const [test, setTest] = useState<{ ruleId: string; recordId: string } | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [aSupprimer, setASupprimer] = useState<NotificationRegleView | null>(null);

  if (port === null || !port.canManage) return null;
  const enregistrer = async (r: NotificationRegleInputView): Promise<void> => {
    if (edition?.id) await port.modifierRegle(edition.id, r);
    else await port.creerRegle(r);
    setEdition(null);
  };
  const tester = async (): Promise<void> => {
    if (!test) return;
    setErreur(null);
    setAvis(null);
    try { setAvis(`Test : ${bilanEnPhrase(await port.testerRegle(test.ruleId, test.recordId.trim()))}`); }
    catch (cause) { setErreur(phrase(cause)); }
  };
  const basculer = async (regle: NotificationRegleView): Promise<void> => {
    setErreur(null);
    try { await port.modifierRegle(regle.id, { actif: !regle.actif }); }
    catch (cause) { setErreur(phrase(cause)); }
  };
  const supprimer = async (regle: NotificationRegleView): Promise<void> => {
    setASupprimer(null);
    setErreur(null);
    try { await port.supprimerRegle(regle.id); }
    catch (cause) { setErreur(phrase(cause)); }
  };

  return (
    <SettingsSection title="Règles" contextLabel="Organisation">
      {port.regles.length === 0 ? (
        <SettingsEmpty>Aucune règle configurée.</SettingsEmpty>
      ) : (
        <SettingsTable
          label="Règles"
          columns={[{ label: 'Règle' }, { label: 'Active', fin: true }, { label: 'Actions', cache: true, fin: true }]}
          rows={port.regles.map((regle) => ({
            id: regle.id,
            attenue: !regle.actif,
            cells: [
              <span key="regle" className="flex min-w-0 flex-col gap-0.5 py-1">
                <span className="flex flex-wrap items-center gap-2 font-medium text-ink-700">
                  {regle.nom + (regle.famille === 'conversations' || regle.famille === 'messages' && regle.type === 'conversation' ? ' (inapplicable aux conversations)' : '')}
                  <StatusBadge tone="neutral" label={regle.actif ? 'active' : 'éteinte'} />
                </span>
                <span className="text-xs text-ink-500">{`${regle.declencheur} · ${regle.famille}${regle.type ? ` (${regle.type})` : ''} · ${regle.canaux.join(', ')}`}</span>
              </span>,
              <Switch key="active" checked={regle.actif} onChange={() => void basculer(regle)} aria-label={`Règle ${regle.nom} active`} />,
              <span key="actions" className="flex flex-wrap justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => { const { id, ...valeur } = regle; setEdition({ id, valeur }); }}>Modifier</Button>
                <Button size="sm" variant="secondary" onClick={() => setTest({ ruleId: regle.id, recordId: '' })}>Tester</Button>
                <Button size="sm" variant="secondary" onClick={() => setASupprimer(regle)}>Supprimer</Button>
              </span>,
            ],
          }))}
        />
      )}
      {test && (
        <SettingsRow label="Tester sur une fiche">
          <SettingsInput value={test.recordId} placeholder="id de la fiche" aria-label="id de la fiche" onChange={(e) => setTest({ ...test, recordId: e.target.value })} />
          <Button size="sm" disabled={test.recordId.trim() === ''} onClick={() => void tester()}>Tester sur cette fiche</Button>
          <Button size="sm" variant="secondary" onClick={() => setTest(null)}>Fermer</Button>
        </SettingsRow>
      )}
      {edition ? (
        <FormulaireRegle initial={edition.valeur} onEnregistrer={enregistrer} onAnnuler={() => setEdition(null)} />
      ) : (
        <SettingsActions><Button size="sm" variant="secondary" onClick={() => setEdition({ id: null, valeur: REGLE_VIDE })}>Ajouter une règle</Button></SettingsActions>
      )}
      {avis && <SettingsMessage>{avis}</SettingsMessage>}
      {erreur && <SettingsMessage tone="error">{erreur}</SettingsMessage>}
      <ConfirmDialog
        open={aSupprimer !== null}
        tone="danger"
        title={`Supprimer la règle « ${aSupprimer?.nom ?? ''} » ?`}
        body={[]}
        confirmLabel="Supprimer"
        onConfirm={() => { if (aSupprimer) void supprimer(aSupprimer); }}
        onCancel={() => setASupprimer(null)}
      />
    </SettingsSection>
  );
}

/* -------------------------------------------------------------------------- */
/* Le panneau                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * PARAMETRES > NOTIFICATIONS : le PERSONNEL d'abord (mes notifications, mes
 * pastilles), puis ce qui arrive dans MyTaki et ce qui en sort, enfin ce que
 * l'organisation ecrit (regles), reserve a qui gere.
 */
export function NotificationsSettingsPanel(): JSX.Element {
  const port = useNotificationsPort();
  return (
    <SettingsPanel>
      <MesNotifications />
      <SectionPastilles />
      <SectionNotificationsExternes />
      {port?.canManage ? <Regles /> : null}
    </SettingsPanel>
  );
}
